import Swal from 'sweetalert2';

/* ── Types ── */
export type ScoreState = 'excelente' | 'muy_activa' | 'activa' | 'en_riesgo' | 'inactiva';

export interface ScoreFactors {
  streamActivo: number;
  publicaciones: number;
  eventos: number;
  respuestasCabina: number;
  lives: number;
  interaccion: number;
}

export interface RadioScoreData {
  score: number;
  state: ScoreState;
  factors: ScoreFactors;
}

/* ── Config ── */
const stateConfig: Record<ScoreState, { label: string; color: string; dot: string; ring: string; track: string; bg: string }> = {
  excelente:    { label: 'Excelente',    color: 'text-emerald-600', dot: 'bg-emerald-500', ring: 'text-emerald-500',   track: 'text-emerald-100',   bg: 'bg-emerald-50' },
  muy_activa:   { label: 'Muy Activa',   color: 'text-green-600',  dot: 'bg-green-500',  ring: 'text-green-500',    track: 'text-green-100',    bg: 'bg-green-50' },
  activa:       { label: 'Activa',       color: 'text-amber-600',  dot: 'bg-amber-500',  ring: 'text-amber-500',    track: 'text-amber-100',    bg: 'bg-amber-50' },
  en_riesgo:    { label: 'En Riesgo',    color: 'text-orange-600', dot: 'bg-orange-500', ring: 'text-orange-500',   track: 'text-orange-100',   bg: 'bg-orange-50' },
  inactiva:     { label: 'Inactiva',     color: 'text-red-600',    dot: 'bg-red-500',    ring: 'text-red-500',      track: 'text-red-100',      bg: 'bg-red-50' },
};

const factorLabels: Record<keyof ScoreFactors, { label: string; icon: string }> = {
  streamActivo:    { label: 'Stream Activo',    icon: 'ri-signal-wifi-line' },
  publicaciones:   { label: 'Publicaciones',    icon: 'ri-article-line' },
  eventos:         { label: 'Eventos',          icon: 'ri-calendar-event-line' },
  respuestasCabina:{ label: 'Resp. en Cabina',  icon: 'ri-chat-3-line' },
  lives:           { label: 'Lives',            icon: 'ri-live-line' },
  interaccion:     { label: 'Interacción',      icon: 'ri-heart-line' },
};

/* ── Helpers ── */
export function getScoreState(score: number): ScoreState {
  if (score >= 80) return 'excelente';
  if (score >= 60) return 'muy_activa';
  if (score >= 40) return 'activa';
  if (score >= 20) return 'en_riesgo';
  return 'inactiva';
}

export function getScoreConfig(score: number) {
  const state = getScoreState(score);
  return { state, config: stateConfig[state] };
}

/* ── Props ── */
interface RadioScoreProps {
  data: RadioScoreData;
  size?: 'sm' | 'md' | 'lg';
  showLabel?: boolean;
  showFactors?: boolean;
  radioName?: string;
}

/* ── Size map ── */
const sizeMap = {
  sm: { container: 'w-[52px] h-[52px]', viewBox: 52, r: 21, stroke: 5, score: 'text-sm', label: 'text-[8px]' },
  md: { container: 'w-[80px] h-[80px]', viewBox: 80, r: 33, stroke: 6, score: 'text-xl', label: 'text-[9px]', outerRing: 40, fontW: 800 },
  lg: { container: 'w-[120px] h-[120px]', viewBox: 120, r: 50, stroke: 9, score: 'text-3xl', label: 'text-[10px]', outerRing: 60, fontW: 800 },
};

export default function RadioScore({ data, size = 'md', showLabel = true, showFactors = false, radioName }: RadioScoreProps) {
  const { score, state, factors } = data;
  const config = stateConfig[state];
  const sm = sizeMap[size];
  const circumference = 2 * Math.PI * sm.r;
  const dashOffset = circumference - (score / 100) * circumference;

  const handleClick = async () => {
    const factorEntries = Object.entries(factors) as [keyof ScoreFactors, number][];
    const factorRows = factorEntries
      .map(([key, val]) => {
        const fl = factorLabels[key];
        const fConfig = getScoreConfig(val);
        const barColor = fConfig.config.ring.replace('text-', '');
        const barHex = getColorHex(barColor);
        return `
          <div style="display:flex;align-items:center;gap:8px;margin-bottom:10px;">
            <div style="width:28px;height:28px;border-radius:8px;background:${barHex}15;display:flex;align-items:center;justify-content:center;flex-shrink:0;">
              <i class="${fl.icon}" style="color:${barHex};font-size:14px;"></i>
            </div>
            <div style="flex:1;min-width:0;">
              <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:3px;">
                <span style="font-size:12px;color:#374151;font-weight:500;">${fl.label}</span>
                <span style="font-size:12px;font-weight:700;color:${barHex};">${val}</span>
              </div>
              <div style="height:4px;background:#f3f4f6;border-radius:2px;overflow:hidden;">
                <div style="height:100%;width:${val}%;background:${barHex};border-radius:2px;transition:width 0.6s ease;"></div>
              </div>
            </div>
          </div>
        `;
      })
      .join('');

    await Swal.fire({
      title: radioName ? `Radio Score · ${radioName}` : 'Radio Score',
      html: `
        <div style="text-align:left;font-size:13px;">
          <div style="text-align:center;margin-bottom:14px;">
            <div style="display:inline-flex;align-items:center;gap:8px;padding:6px 14px;background:${getColorHex(config.ring.replace('text-', ''))}15;border-radius:12px;">
              <span style="font-size:28px;font-weight:800;color:${getColorHex(config.ring.replace('text-', ''))};">${score}</span>
              <span style="display:flex;align-items:center;gap:4px;font-size:13px;font-weight:700;color:${getColorHex(config.ring.replace('text-', ''))};">
                <span style="width:8px;height:8px;border-radius:50%;background:${getColorHex(config.dot.replace('bg-', ''))};display:inline-block;"></span>
                ${config.label}
              </span>
            </div>
          </div>
          <p style="margin-bottom:12px;color:#6b7280;font-size:11px;">
            El Radio Score mide la actividad general de la radio basado en 6 factores clave. Se actualiza cada hora.
          </p>
          ${factorRows}
        </div>
      `,
      confirmButtonText: 'Cerrar',
      confirmButtonColor: '#1f2937',
      customClass: {
        popup: 'rounded-2xl',
        title: 'font-bold text-sm',
        confirmButton: 'rounded-lg px-5 py-2 text-sm font-bold whitespace-nowrap',
        htmlContainer: 'text-sm',
      },
      buttonsStyling: false,
    });
  };

  return (
    <div
      className={`${sm.container} relative shrink-0 cursor-pointer group`}
      onClick={handleClick}
      title={`Radio Score: ${score} · ${config.label}`}
    >
      <svg className="w-full h-full -rotate-90" viewBox={`0 0 ${sm.viewBox} ${sm.viewBox}`}>
        {/* Track */}
        <circle
          cx={sm.viewBox / 2}
          cy={sm.viewBox / 2}
          r={sm.r}
          fill="none"
          stroke="currentColor"
          className={config.track}
          strokeWidth={sm.stroke}
        />
        {/* Score arc */}
        <circle
          cx={sm.viewBox / 2}
          cy={sm.viewBox / 2}
          r={sm.r}
          fill="none"
          stroke="currentColor"
          className={`${config.ring} transition-all duration-700 ease-out`}
          strokeWidth={sm.stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
        />
      </svg>
      {/* Center content */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`${sm.score} font-extrabold ${config.color} leading-none`}>
          {score}
        </span>
        {showLabel && (
          <span className={`${sm.label} ${config.color} font-bold mt-0.5 whitespace-nowrap`}>
            {config.label}
          </span>
        )}
      </div>

      {/* Factors bars (optional, for standalone use) */}
      {showFactors && (
        <div className="mt-3 space-y-1.5">
          {(Object.entries(factors) as [keyof ScoreFactors, number][]).map(([key, val]) => {
            const fl = factorLabels[key];
            const fConfig = getScoreConfig(val);
            return (
              <div key={key} className="flex items-center gap-2">
                <div className="w-5 h-5 rounded-md bg-background-100 flex items-center justify-center shrink-0">
                  <i className={`${fl.icon} ${fConfig.config.color} text-[10px]`}></i>
                </div>
                <span className="text-foreground-500 text-[10px] w-[80px] truncate">{fl.label}</span>
                <div className="flex-1 h-1.5 bg-background-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full ${fConfig.config.ring.replace('text-', 'bg-')}`}
                    style={{ width: `${val}%`, transition: 'width 0.6s ease' }}
                  ></div>
                </div>
                <span className="text-foreground-400 text-[9px] w-7 text-right font-bold">{val}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ── Tailwind doesn't support dynamic colors, so we need a helper for hex ── */
function getColorHex(cssClass: string): string {
  const map: Record<string, string> = {
    'emerald-500': '#10b981',
    'emerald-100': '#d1fae5',
    'green-500': '#22c55e',
    'green-100': '#dcfce7',
    'amber-500': '#f59e0b',
    'amber-100': '#fef3c7',
    'orange-500': '#f97316',
    'orange-100': '#ffedd5',
    'red-500': '#ef4444',
    'red-100': '#fee2e2',
  };
  return map[cssClass] || '#6b7280';
}